import React, { memo, useCallback } from "react";
import {
  Grid,
  FormControl,
  FormLabel,
  FormGroup,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import {
  FormField,
  RadioButtonGroup,
  LabeledTextField,
  PackageTypeSelect,
  CommodityField,
} from "../shared/form";
import TooltipWithClickAway from "../terminalPicker/TooltipWithClickAway";
import NoteBox from "../noteBox/NoteBox";
import { MisdeliveryFormData } from "../../models/misDelivery";
import { FORM_STYLES, FORM_CLASSES, XL_GRID_PROPS } from "../../constants";
import { IFilterDropdown } from "../../AppState/overagesFilterFormActions";
import { useMisdeliveryFormContext } from "../../contexts/MisdeliveryFormContext";

interface ShipmentDetailsSectionProps {
  packageTypeOptions?: IFilterDropdown[];
  commodityOptions?: IFilterDropdown[];
}

// Constants
const FIELD_GRID_PROPS = { md: 3, xs: 12 } as const;
const LARGE_FIELD_GRID_PROPS = { md: 6, xs: 12 } as const;
const FULL_FIELD_GRID_PROPS = { md: 12, xs: 12 } as const;

// Memoized NoteBox component
const HazMatNote = memo(() => (
  <Grid item {...FULL_FIELD_GRID_PROPS} className="mt-10">
    <NoteBox message="Note: BOL for misdelivery pick up needs to contain all DOT required information. Please contact catkinson@saia.com" />
  </Grid>
));
HazMatNote.displayName = "HazMatNote";

// Helper: Check if field has error
const hasFieldError = (
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  fieldName: string
): boolean => {
  return !!(touched[fieldName] && errors[fieldName]);
};

// Helper: Get field helper text
const getFieldHelperText = (
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  fieldName: string
): string | undefined => {
  return touched[fieldName] ? errors[fieldName] : undefined;
};

// Helper: Create radio field change handler
const createRadioChangeHandler = (
  fieldName: keyof MisdeliveryFormData,
  onFieldChange: (field: keyof MisdeliveryFormData, value: any) => void
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  onFieldChange(fieldName, e.target.value);
};

const ShipmentDetailsSection: React.FC<ShipmentDetailsSectionProps> = memo(
  ({ packageTypeOptions = [], commodityOptions }) => {
    // Get form state from context
    const { formData, errors, touched, onFieldChange, onFieldBlur, handleChange, handleBlur, disabled } = useMisdeliveryFormContext();
    
    /* NOSONAR */
    // Field change handlers
    const handleFieldChange = useCallback(
      (fieldName: keyof MisdeliveryFormData) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
          onFieldChange(fieldName, e.target.value);
        },
      [onFieldChange],
    );

    // Generic blur handler for fields that need to be marked as touched
    const handleFieldBlur = useCallback(
      (fieldName: keyof MisdeliveryFormData) => () => onFieldBlur?.(fieldName),
      [onFieldBlur],
    );

    const handlePackageTypeChange = useCallback(
      (value: string) => {
        onFieldChange("packageType", value);
      },
      [onFieldChange],
    );

    const handleCanProvidePhotosChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFieldChange("canProvidePhotos", e.target.value);
      },
      [onFieldChange],
    );

    // Helper to render FormField with common props
    const renderFormField = useCallback(
      (
        id: keyof MisdeliveryFormData,
        label: string,
        placeholder: string,
        options: {
          maxLength?: number;
          required?: boolean;
          type?: "number" | "text" | "email";
          disabled?: boolean;
          gridProps?: any;
          onBlur?: () => void;
          multiline?: boolean,
          rows? : number,
        } = {},
      ) => {
        let value = formData[id] ?? "";
        if (options.type === "number" && value !== "") {
          value = Number(value);
        }
        return (
          <FormField
            id={id}
            label={label}
            value={value}
            onChange={handleChange}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              handleBlur(event);
              options.onBlur?.();
            }}
            placeholder={placeholder}
            maxLength={options.maxLength}
            required={options.required}
            type={options.type}
            disabled={options.disabled}
            error={hasFieldError(touched, errors, id)}
            helperText={getFieldHelperText(touched, errors, id)}
            gridProps={options.gridProps}
            multiline={options.multiline}
            rows={options.rows}
          />
        );
      },
      [formData, touched, errors, handleBlur, handleChange],
    );

    // Helper to render LabeledTextField with common props
    const renderLabeledTextField = useCallback(
      (
        id: keyof MisdeliveryFormData,
        label: string,
        placeholder: string,
        options: {
          maxLength?: number;
          required?: boolean;
          type?: "number" | "text" | "email";
          disabled?: boolean;
          gridProps?: any;
          tooltip?: { text: string; key: string };
          multiline?: boolean;
          rows?: number;
        } = {},
      ) => (
        <LabeledTextField
          id={id}
          label={label}
          value={formData[id] ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={options.maxLength}
          required={options.required}
          type={options.type}
          disabled={options.disabled}
          error={hasFieldError(touched, errors, id)}
          helperText={getFieldHelperText(touched, errors, id)}
          gridProps={options.gridProps}
          tooltip={options.tooltip}
          multiline={options.multiline}
          rows={options.rows}
        />
      ),
      [formData, touched, errors, handleChange, handleBlur],
    );

    return (
      <Grid container spacing={2} className="filter-margin">
        {/* All fields except additionalNotes and f18ProNumber are disabled in f18NoteMode */}

        {renderFormField(
          "totalPiecesMisdelivered",
          "Total Pieces Misdelivered?",
          "Enter Total Pieces Misdelivered",
          {
            type: "number",
            maxLength: 5,
            required: true,
            gridProps: FIELD_GRID_PROPS,
            disabled: disabled,
          },
        )}

         <PackageTypeSelect
          id="packageType"
          label="Package Type"
          value={formData.packageType ?? ""}
          onChange={handlePackageTypeChange}
          onBlur={handleFieldBlur("packageType")}
          options={packageTypeOptions}
          error={hasFieldError(touched, errors, "packageType")}
          helperText={getFieldHelperText(touched, errors, "packageType")}
          required
          gridProps={FIELD_GRID_PROPS}
          disabled={disabled}
        />


        {renderFormField(
          "shrinkWrappedPallets",
          "How many Shrink Wrapped Pallets?",
          "Enter Shrink Wrapped Pallets",
          {
            maxLength: 2,
            type: "number",
            required: true,
            gridProps: FIELD_GRID_PROPS,
            disabled: disabled,
          },
        )}

        <CommodityField
          id="commodityCode"
          key={formData.commodityCode}
          label="Commodity"
          value={formData.commodityCode}
          onChange={(_, value) => {
            onFieldChange("commodityCode", value?.code ?? "");
          }}
          placeholder="Select commodity code"
          required
          onBlur={handleFieldBlur("commodityCode")}
          error={touched.commodityCode && !!errors.commodityCode}
          helperText={touched.commodityCode ? errors.commodityCode : undefined}
          gridProps={FIELD_GRID_PROPS}
          options={commodityOptions}
          disabled={disabled}
        />

        {renderFormField(
          "commodityDescription",
          "MSD commodity Description (Item #, Carton #, Serial #)",
          "Enter MSD Commodity Description",
          {
            type: "text",
            maxLength: 100,
            required: true,
            gridProps: LARGE_FIELD_GRID_PROPS,
            disabled,
          },
        )}
        <Grid item md={6} xs={12}>
          <Grid item {...FULL_FIELD_GRID_PROPS} style={{ display: "flex" }}>
            <RadioButtonGroup
              id="isMisdeliveryHazmat"
              label="Is the Misdelivery HazMat?"
              value={formData.isMisdeliveryHazmat}
              onChange={handleFieldChange("isMisdeliveryHazmat")}
              onBlur={handleBlur}
              required
              error={  !["Y", "N"].includes(formData.isMisdeliveryHazmat ?? "") && hasFieldError(touched, errors, "isMisdeliveryHazmat")}
              helperText={!["Y", "N"].includes(formData.isMisdeliveryHazmat ?? "") ? getFieldHelperText(touched, errors, "isMisdeliveryHazmat") : undefined }
              gridProps={LARGE_FIELD_GRID_PROPS}
              disabled={disabled}
            />

            {formData.isMisdeliveryHazmat === "Y" &&
              renderLabeledTextField(
                "unNumber",
                "UN/NA#",
                "Enter UN/NA#",
                {
                  required: true,
                  maxLength: 6,
                  gridProps: LARGE_FIELD_GRID_PROPS,
                  tooltip: {
                    text: "For guidance on how to fill out HAZMAT BOL, please refer to your hazmat BOL module on Workday",
                    key: "unNumberTooltip",
                  },
                  disabled,
                },
              )}
          </Grid>

          {formData.isMisdeliveryHazmat === "Y" && (
            <Grid container spacing={2} className="filter-margin">
              <HazMatNote />
            </Grid>
          )}
        </Grid>

        <Grid item {...FIELD_GRID_PROPS}>
          <FormControl
            className={FORM_CLASSES.FORM_CONTROL}
            error={hasFieldError(touched, errors, "productMixedWithPallet")}
            disabled={disabled}
          >
            <FormLabel
              id="productMixedWithPallet"
              htmlFor="productMixedWithPalletRadioGroup"
              style={FORM_STYLES.LABEL}
            >
              Was the product mixed in with the shrink wrapped pallet? <sup>*</sup>
              &nbsp;
            </FormLabel>
            <FormGroup className="shift-left" sx={{ marginLeft: 1 }}>
              <RadioGroup
                row
                aria-labelledby="productMixedWithPallet"
                name="productMixedWithPallet"
                id="productMixedWithPalletRadioGroup"
                value={formData.productMixedWithPallet}
                onBlur={handleBlur}
                onChange={createRadioChangeHandler("productMixedWithPallet", onFieldChange)}
                sx={{ paddingTop: 1 }}
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio />}
                  label="Yes"
                  name="productMixedWithPallet"
                  className="mt-0"
                  checked={formData.productMixedWithPallet === "Y"}
                  disabled={disabled}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio />}
                  label="No"
                  name="productMixedWithPallet"
                  className="mt-0"
                  checked={formData.productMixedWithPallet === "N"}
                  disabled={disabled}
                />
              </RadioGroup>
            </FormGroup>
            {errors.productMixedWithPallet &&
              touched.productMixedWithPallet && (
                <span className={"form-error"}>
                  {errors.productMixedWithPallet}
                </span>
              )}
          </FormControl>
        </Grid>
        <Grid item {...FIELD_GRID_PROPS}>
          <FormControl
            className={FORM_CLASSES.FORM_CONTROL}
            error={hasFieldError(touched, errors, "looseWithShipment")}
            disabled={disabled}
          >
            <FormLabel
              id="looseWithShipment"
              htmlFor="looseWithShipmentRadioGroup"
              style={FORM_STYLES.LABEL}
            >
              Was this loose with their shipment? <sup>*</sup>
              &nbsp;
            </FormLabel>
            <FormGroup className="shift-left" sx={{ marginLeft: 1 }}>
              <RadioGroup
                row
                aria-labelledby="looseWithShipment"
                name="looseWithShipment"
                id="looseWithShipmentRadioGroup"
                value={formData.looseWithShipment}
                onChange={createRadioChangeHandler("looseWithShipment", onFieldChange)}
                onBlur={handleBlur}
                sx={{ paddingTop: 1 }}
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio />}
                  label="Yes"
                  name="looseWithShipment"
                  className="mt-0"
                  checked={formData.looseWithShipment === "Y"}
                  disabled={disabled}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio />}
                  label="No"
                  name="looseWithShipment"
                  className="mt-0"
                  checked={formData.looseWithShipment === "N"}
                  disabled={disabled}
                />
              </RadioGroup>
            </FormGroup>
            {errors.looseWithShipment && touched.looseWithShipment && (
              <span className={"form-error"}>{errors.looseWithShipment}</span>
            )}
          </FormControl>
        </Grid>

        <Grid item {...FIELD_GRID_PROPS}>
          <FormControl
            className={FORM_CLASSES.FORM_CONTROL}
            error={hasFieldError(touched, errors, "canProvidePhotos")}
            disabled={disabled}
          >
            <FormLabel
              id="canProvidePhotosLabel"
              htmlFor="canProvidePhotosRadioGroup"
              style={FORM_STYLES.LABEL}
            >
              Can you provide photos? <sup>*</sup>
              &nbsp;
              <TooltipWithClickAway
                tooltipText="When pictures are received, upload to MSD exception under the Upload documents section"
                tooltipKey="canProvidePhotos"
              />
            </FormLabel>
            <FormGroup className="shift-left" sx={{ marginLeft: 1 }}>
              <RadioGroup
                row
                aria-labelledby="canProvidePhotosLabel"
                name="canProvidePhotos"
                id="canProvidePhotosRadioGroup"
                value={formData.canProvidePhotos}
                onChange={handleCanProvidePhotosChange}
                onBlur={handleBlur}
                sx={{ paddingTop: 1 }}
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio />}
                  label="Yes"
                  name="canProvidePhotos"
                  className="mt-0"
                  checked={formData.canProvidePhotos === "Y"}
                  disabled={disabled}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio />}
                  label="No"
                  name="canProvidePhotos"
                  className="mt-0"
                  checked={formData.canProvidePhotos === "N"}
                  disabled={disabled}
                />
              </RadioGroup>
            </FormGroup>
            {hasFieldError(touched, errors, "canProvidePhotos") && (
              <FormHelperText sx={{ marginTop: 0.5, marginLeft: 1 }}>
                {errors.canProvidePhotos}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {renderLabeledTextField(
          "freightLabel",
          "How was the Freight Labeled",
          "Enter Details here",
          {
            required: true,
            maxLength: 180,
            gridProps: XL_GRID_PROPS,
            tooltip: {
              text: "If the packing slip is different than the shipping label address, please contact OSDCLP@saia.com. Proper NMFC shipping label is required. If not there, there is a shipping error.",
              key: "freightLabel",
            },
            disabled,
          },
        )}
        <FormField
          id="msdDescription"
          label="MSD Description: When/where was the Misdelivery first discovered? Give a complete description of MSD product/commodity and packaging."
          value={formData.msdDescription}
          onChange={handleFieldChange("msdDescription")}
          placeholder="Enter Details here"
          maxLength={180}
          onBlur={() => onFieldBlur?.("msdDescription")}
          required
          error={touched.msdDescription && !!errors.msdDescription}
          helperText={
            touched.msdDescription ? errors.msdDescription : undefined
          }
          gridProps={FULL_FIELD_GRID_PROPS}
          disabled={disabled}
        />

        <RadioButtonGroup
          id="pickupWillLeaveShort"
          label="When we pick up the misdelivered product, will this result in a shortage at the Pro location due to the misdelivery?"
          value={formData.pickupWillLeaveShort}
          onChange={handleFieldChange("pickupWillLeaveShort")}
          onBlur={handleBlur}
          required
          error={!["Y", "N"].includes(formData.pickupWillLeaveShort ?? "") && touched.pickupWillLeaveShort && !!errors.pickupWillLeaveShort}
          helperText={
            !["Y", "N"].includes(formData.pickupWillLeaveShort ?? "") &&
            touched.pickupWillLeaveShort
              ? errors.pickupWillLeaveShort
              : undefined
          }
          gridProps={LARGE_FIELD_GRID_PROPS}
          disabled={disabled}
        />

        {formData.pickupWillLeaveShort === "Y" &&
          renderFormField(
            "shortageDescription",
            "Piece Count and Description of shortage",
            "Enter Piece Count and Description of shortage",
            {
              maxLength: 180,
              required: true,
              gridProps: LARGE_FIELD_GRID_PROPS,
              onBlur: () => onFieldBlur?.("shortageDescription"),
              disabled,
              multiline: true,
              rows: 2,
            },
          )}

        {/* F18 Note mode: only show and enable additionalNotes and f18ProNumber */}
        <Grid
          container
          spacing={2}
          className="filter-margin padding-15 pr-0 mb-0"
        >
          <>
            {renderFormField(
              "confirmPalletCountAtInitialDelivery",
              "Confirm pallet count at initial delivery",
              "Enter confirm pallet count",
              {
                type: "number",
                maxLength: 2,
                required: true,
                gridProps: {md : 2.8, xs: 12},
                disabled,
              },
            )}
            {renderFormField(
              "callTakenBy",
              "Call taken by",
              "Enter call taken by",
              {
                type: "text",
                maxLength: 40,
                gridProps: {md : 3.2, xs: 12},
                onBlur: () => onFieldBlur?.("callTakenBy"),
                required: true,
                disabled,
              },
            )}
            {renderFormField(
              "otherInformation",
              "Any other information",
              "Enter additional notes",
              {
                maxLength: 180,
                gridProps: LARGE_FIELD_GRID_PROPS,
                onBlur: () => onFieldBlur?.("otherInformation"),
                disabled,
                multiline: true,
                rows: 2,
              },
            )}
          </>
        </Grid>
      </Grid>
    );
  },
);

ShipmentDetailsSection.displayName = "ShipmentDetailsSection";

export default ShipmentDetailsSection;
